import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

/**
 * The dashboard pages a `nexus login` opens. The backend builds `authorizeUrl` and
 * `verificationUrl` from these; the frontend routes them.
 */
export const CLI_LOGIN_AUTHORIZE_PAGE_PATH = "/cli/authorize";
export const CLI_LOGIN_DEVICE_PAGE_PATH = "/cli/device";

const CliLoginOrganizationSchema = z.object({ id: z.string(), name: z.string() });

/** What a person is shown before answering, so they see what they are approving. */
const CliLoginViewSchema = z.object({
  /** The environment the key will be written for, as the CLI named it. */
  environment: z.string(),
  expiresAt: z.string(),
  /** The signed-in user's organizations; the one picked here owns the key. */
  organizations: z.array(CliLoginOrganizationSchema)
});

const CliLoginSessionViewSchema = CliLoginViewSchema.extend({
  /** `127.0.0.1:53111`: the address on this machine the login lands on. */
  loopback: z.string()
});

const CliLoginSessionPathVarsSchema = z.object({ sessionId: z.string().min(1).max(64) });
const organizationId = z.string().min(1).max(255);
/** Typed by a person, so the separator and the case are theirs to get wrong. */
const userCode = z.string().min(8).max(32);

/** Only the browser's next stop. It holds the one-time `code`, so it is never cached. */
const CliLoginRedirectSchema = z.object({ redirectUrl: z.string() });

/**
 * ZCliLogin — the dashboard's half of `nexus login` (F-PROTO-REST-CLI-AUTH): a signed-in
 * person answering a login a CLI started. Authorization is the Clerk session and
 * nothing else. The `/api/sdk/v1/cli/auth/*` routes the CLI calls are a machine surface
 * with their own wire format, so they are deliberately absent from this contract.
 *
 * The device routes POST the user code even to read it, so that it stays out of URLs,
 * access logs and browser history.
 */
export const ZCliLogin = {
  /** GET /api/cli-login/sessions/:sessionId — what the authorize page shows. */
  GetSession: {
    method: "GET" as const,
    path: "/api/cli-login/sessions/:sessionId",
    PathVars: CliLoginSessionPathVarsSchema,
    Response: ApiSuccessSchema(CliLoginSessionViewSchema)
  },

  /** POST /api/cli-login/sessions/:sessionId/approve */
  ApproveSession: {
    method: "POST" as const,
    path: "/api/cli-login/sessions/:sessionId/approve",
    PathVars: CliLoginSessionPathVarsSchema,
    Body: z.object({ organizationId }),
    Response: ApiSuccessSchema(CliLoginRedirectSchema)
  },

  /** POST /api/cli-login/sessions/:sessionId/deny */
  DenySession: {
    method: "POST" as const,
    path: "/api/cli-login/sessions/:sessionId/deny",
    PathVars: CliLoginSessionPathVarsSchema,
    Response: ApiSuccessSchema(CliLoginRedirectSchema)
  },

  /** POST /api/cli-login/device/lookup — what the verification page shows for a typed code. */
  LookupDevice: {
    method: "POST" as const,
    path: "/api/cli-login/device/lookup",
    Body: z.object({ userCode }),
    Response: ApiSuccessSchema(CliLoginViewSchema)
  },

  /** POST /api/cli-login/device/approve */
  ApproveDevice: {
    method: "POST" as const,
    path: "/api/cli-login/device/approve",
    Body: z.object({ userCode, organizationId }),
    Response: ApiSuccessSchema(z.object({ approved: z.literal(true) }))
  },

  /** POST /api/cli-login/device/deny */
  DenyDevice: {
    method: "POST" as const,
    path: "/api/cli-login/device/deny",
    Body: z.object({ userCode }),
    Response: ApiSuccessSchema(z.object({ denied: z.literal(true) }))
  }
} as const;
